import { z } from 'zod';

export enum Rating {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
}

export enum FeedbackCategory {
  IrrelevantInformation = 'irrelevantInformation',
  MissingInformation = 'missingInformation',
  InsufficientDetail = 'insufficientDetail',
  IncorrectInformation = 'incorrectInformation',
  Other = 'other',
}

export enum ChatRole {
  User = 'user',
  Administrator = 'administrator',
  Assistant = 'assistant',
  Status = 'status',
}

export enum MessageGenerationStatus {
  Completed = 'completed',
  Stopped = 'stopped',
  Failed = 'failed',
  AwaitingUser = 'awaiting_user',
}

export enum MessageStopReason {
  Complete = 'complete',
  StepLimit = 'step_limit',
  Guard = 'guard',
  Drained = 'drained',
  Failed = 'failed',
  Aborted = 'aborted',
  ClientDisconnect = 'client_disconnect',
  ServerTimeout = 'server_timeout',
}

/** Stable machine-readable failures for the run envelope and persistence. */
export enum RunErrorCode {
  Aborted = 'aborted',
  AgentFailed = 'agent_failed',
  ClientDisconnect = 'client_disconnect',
  ServerTimeout = 'server_timeout',
}

/** Stable classification used by tool execution, persistence, and telemetry. */
export enum ToolErrorKind {
  Auth = 'auth',
  Cancelled = 'cancelled',
  Policy = 'policy',
  Timeout = 'timeout',
  Tool = 'tool',
  Transport = 'transport',
  Validation = 'validation',
}

/**
 * Stable machine-readable tool failures crossing the agent protocol boundary.
 * Add a new value only when callers need to distinguish a genuinely different
 * recovery path; provider/server prose never becomes a code dynamically.
 */
export enum ToolErrorCode {
  DocumentSearchFailed = 'document_search_failed',
  DuplicateToolCall = 'duplicate_tool_call',
  ModerationBlocked = 'moderation_blocked',
  FetchErrorStatus = 'fetch_error_status',
  FetchTimeout = 'fetch_timeout',
  FetchTransportError = 'fetch_transport_error',
  HttpErrorStatus = 'http_error_status',
  InvalidToolArguments = 'invalid_tool_arguments',
  InvalidUrl = 'invalid_url',
  McpAuthRequired = 'mcp_auth_required',
  McpConnectionBudgetExceeded = 'mcp_connection_budget_exceeded',
  McpOauthCommitFailed = 'mcp_oauth_commit_failed',
  McpRunClosed = 'mcp_run_closed',
  McpToolError = 'mcp_tool_error',
  ShopCategoriesFailed = 'shop_categories_failed',
  ShopSearchFailed = 'shop_search_failed',
  ToolAuthenticationFailed = 'tool_authentication_failed',
  ToolCancelled = 'tool_cancelled',
  ToolFailed = 'tool_failed',
  ToolNotAvailable = 'tool_not_available',
  ToolTimeout = 'tool_timeout',
  ToolTransportError = 'tool_transport_error',
  UnsafeToolUrl = 'unsafe_tool_url',
  UnsafeUrl = 'unsafe_url',
  UrlDomainNotAllowed = 'url_domain_not_allowed',
  WebSearchDisabled = 'web_search_disabled',
  WebSearchFailed = 'web_search_failed',
  WebSearchNotConfigured = 'web_search_not_configured',
}

export enum Role {
  SuperAdmin = 'superAdmin',
  Admin = 'admin',
}

export enum RoleWOSuperAdmin {
  Admin = 'admin',
}

export enum Agent {
  Conversational = 'conversational',
  ShopAssistant = 'shopAssistant',
}

/**
 * OpenAI: https://platform.openai.com/docs/models/gpt-4o
 * Anthropic: https://docs.anthropic.com/en/docs/about-claude/models#model-comparison-table
 * openrouter.ai: https://openrouter.ai/
 */
export enum ChatModel {
  // OpenAI models
  Gpt61Sol = 'gpt-6.1-sol',
  Gpt6Luna = 'gpt-6-luna',
  Gpt56Sol = 'gpt-5.6-sol',
  Gpt56Terra = 'gpt-5.6-terra',
  Gpt56Luna = 'gpt-5.6-luna',
  Gpt55 = 'gpt-5.5',
  Gpt54 = 'gpt-5.4',
  Gpt54Mini = 'gpt-5.4-mini',
  Gpt54Nano = 'gpt-5.4-nano',
  Gpt41 = 'gpt-4.1',
  Gpt41Mini = 'gpt-4.1-mini',
  Gpt41Nano = 'gpt-4.1-nano',
  Gpt5 = 'gpt-5',
  Gpt5Mini = 'gpt-5-mini',
  Gpt5Nano = 'gpt-5-nano',
  GptO3Mini = 'o3-mini-2025-01-31',

  // OpenRouter.ai models
  Grok46 = 'x-ai/grok-4.6',
  Grok45 = 'x-ai/grok-4.5',
  Grok4Fast = 'x-ai/grok-4-fast',
  Grok41Fast = 'x-ai/grok-4.1-fast',
  Grok4 = 'x-ai/grok-4',
  Grok420 = 'x-ai/grok-4.20',
  Grok43 = 'x-ai/grok-4.3',
  Gemini2Flash = 'google/gemini-2.0-flash-001',
  Gemini2FlashLite = 'google/gemini-2.0-flash-lite-001',
  Gemini25Flash = 'google/gemini-2.5-flash',
  Gemini25FlashLite = 'google/gemini-2.5-flash-lite',
  Gemini25Pro = 'google/gemini-2.5-pro',
  Gemini3FlashPreview = 'google/gemini-3-flash-preview',
  Gemini31FlashLite = 'google/gemini-3.1-flash-lite',
  Gemini31FlashLitePreview = 'google/gemini-3.1-flash-lite-preview',
  Gemini37Flash = 'google/gemini-3.7-flash',
  Gemini36Flash = 'google/gemini-3.6-flash',
  Gemini35Flash = 'google/gemini-3.5-flash',
  Gemini35FlashLite = 'google/gemini-3.5-flash-lite',
  Gemini31ProPreview = 'google/gemini-3.1-pro-preview',
  Claude35Sonnet = 'claude-3-5-sonnet-latest', // Anthropic direct
  ClaudeSonnet55 = 'anthropic/claude-sonnet-5.5',
  ClaudeSonnet5 = 'anthropic/claude-sonnet-5',
  ClaudeSonnet46 = 'anthropic/claude-sonnet-4.6',
  ClaudeOpus47 = 'anthropic/claude-opus-4.7',
  ClaudeOpus48 = 'anthropic/claude-opus-4.8',
  ClaudeOpus48Fast = 'anthropic/claude-opus-4.8-fast',
  ClaudeOpus5 = 'anthropic/claude-opus-5',
  ClaudeHaiku55 = 'anthropic/claude-haiku-5.5',
  ClaudeHaiku45 = 'anthropic/claude-haiku-4.5',
  Glm52 = 'z-ai/glm-5.2',
  DeepSeekV4Pro = 'deepseek/deepseek-v4-pro',
  DeepSeekV4Flash = 'deepseek/deepseek-v4-flash',
  DeepSeekV41Flash = 'deepseek/deepseek-v4.1-flash',
  MiMoV25 = 'xiaomi/mimo-v2.5',
  MiMoV25Pro = 'xiaomi/mimo-v2.5-pro',
  MiniMaxM3 = 'minimax/minimax-m3',
  Step37Flash = 'stepfun/step-3.7-flash',
  HunyuanHy3 = 'tencent/hy3',
  Gemini38Flash = 'google/gemini-3.8-flash',
  MuseSpark13 = 'meta/muse-spark-1.3',
}

/**
 * Embedding models
 */
export enum EmbeddingModel {
  // OpenAI models
  TextEmbeddingAda002 = 'text-embedding-ada-002',
  TextEmbedding3Small = 'text-embedding-3-small',
  TextEmbedding3Large = 'text-embedding-3-large',

  // JinaAI models
  JinaEmbeddingsV3 = 'jina-embeddings-v3',
}

export enum ContentType {
  Website = 'website',
  Document = 'document',
  ExternalDocument = 'externalDocument',
}

/**
 * Origin of a document/folder — where the content was discovered from.
 * Orthogonal to {@link ContentType}, which describes how the content is
 * parsed: a PDF linked on a tracked website is `ExternalDocument` (format)
 * with `Website` source (origin). Shared discriminator for website tracking,
 * Google Drive sync, and RSS feed sync sources.
 */
export enum SourceType {
  Upload = 'upload',
  Website = 'website',
  GoogleDrive = 'google_drive',
  Rss = 'rss',
}

export enum ToolType {
  Text = 'text',
  Tool = 'tool',
}

export enum TonePreset {
  Friendly = 'friendly',
  Casual = 'casual',
  Balanced = 'balanced',
  Professional = 'professional',
  Formal = 'formal',
}

export enum ResponseLengthPreset {
  VeryShort = 'very_short',
  Short = 'short',
  Medium = 'medium',
  Long = 'long',
  VeryLong = 'very_long',
}

export enum Language {
  Czech = 'cs',
  English = 'en',
}

export enum DocumentStatus {
  ParsingError = 'parsingError',
  EmbeddingError = 'embeddingError',
  Uploading = 'uploading',
  Ready = 'ready',
  Parsing = 'parsing',
  ParsingAsync = 'parsingAsync', // Represents a job that is scheduled but not yet completed
  Parsed = 'parsed',
  Embedding = 'embedding',
  Done = 'done',
}

export enum JobProvider {
  OpenAI = 'openai',
  LlamaParse = 'llamaparse',
  Firecrawl = 'firecrawl',
  Mistral = 'mistral',
}

/**
 * Categories of usage records.
 */
// How about more categories? (Voice/Chat instead of agent)
export enum RecordCategory {
  Data = 'data',
  Agent = 'agent',
  Other = 'other',
  RAG = 'rag',
}

/**
 * Types of usage records. This identifies the actual operation that was performed.
 * Llm for LLM calls, Embedding for embedding calls, Parsing for parsing calls, etc.
 */
export enum RecordType {
  // All calls to the LLMs (agent nodes, chat calls, etc.)
  Llm = 'llm',

  // All calls to the embedding models
  Embedding = 'embedding',

  // All calls to the parsing models/services/tools
  Parsing = 'parsing',

  // All calls to the parsing models/services/tools
  Scraping = 'scraping',

  // All calls to the reranking models
  Rerank = 'rerank',

  // All other types
  Other = 'other',
}

/**
 * Types of units that can be used to track usage.
 */
export enum RecordUnit {
  Tokens = 'tokens',
  Characters = 'characters',
  Seconds = 'seconds',
  Credits = 'credits',
}

/**
 * Providers of the models/services/tools.
 */
export enum Provider {
  OpenAI = 'openai',
  OpenRouter = 'openrouter',
  Cohere = 'cohere',
  Anthropic = 'anthropic',
  Firecrawl = 'firecrawl',
  LlamaParse = 'llamaparse',
  JinaAI = 'jinaai',
  Twilio = 'twilio',
  Mistral = 'mistral',
  Google = 'google',
}

/**
 * Modes of the thread. Either user talks to human or AI.
 */
export enum ThreadMode {
  Human = 'human',
  AI = 'ai',
}

/**
 * Categories of the thread. Either voice or chat.
 */
export enum ThreadCategory {
  Voice = 'voice',
  Chat = 'chat',
}

/**
 * Types of agents.
 */
export enum AgentType {
  Chat = 'chat',
  Voice = 'voice',
}

/**
 * How Kanbu authenticates to an MCP server.
 */
export enum McpAuthMode {
  None = 'none',
  StaticBearer = 'static_bearer',
  McpOAuth = 'mcp_oauth',
}

/**
 * Denormalized connection state for an MCP integration, shown in admin UI.
 */
export enum McpConnectionStatus {
  NotConnected = 'not_connected',
  Connected = 'connected',
  Expired = 'expired',
  Error = 'error',
}

/**
 * Names of the entities that can be associated with usage records.
 */
export enum EntityType {
  Document = 'document',
  ChatMessage = 'chatMessage',
  Thread = 'thread',
  Chat = 'chat',
  Feed = 'feed',
}

export enum ChatPlacementPosition {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export enum ChatDisplayMode {
  Widget = 'widget',
  Immersive = 'immersive',
}

export enum Gender {
  None = 'none',
  Male = 'male',
  Female = 'female',
}

export enum ThemePreset {
  Light = 'light',
  Dark = 'dark',
}

export enum SourcesFormat {
  Simple = 'simple',
  Academic = 'academical',
}

export enum SourcesDisplayMode {
  /**
   * Compact source chips ("pills") shown under the answer. The stored value
   * stays `'inline'` for backward compatibility with existing chat configs —
   * the member is named `Pills` to avoid confusion with `inlineCitations`
   * (the separate toggle for inline `[cN]` superscripts inside the text).
   */
  Pills = 'inline',
  Structured = 'structured',
}

export enum ChatDomainPolicyMode {
  All = 'all',
  Whitelist = 'whitelist',
  Blacklist = 'blacklist',
}

export enum SubscriptionTier {
  Free = 'Free',
  Starter = 'Starter',
  Basic = 'Basic',
  Standard = 'Standard',
  Premium = 'Premium',
  Enterprise = 'Enterprise',
}

export enum SubscriptionFeature {
  KbAutoUpdates = 'kb_auto_updates',
}

/**
 * Subscription Statuses aligned with Stripe.
 * See: https://stripe.com/docs/api/subscriptions/object#subscription_object-status
 */
export enum SubscriptionStatus {
  Active = 'active',
  Canceled = 'canceled',
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired',
  PastDue = 'past_due',
  Trialing = 'trialing',
  Unpaid = 'unpaid',
  // 'paused' is another potential Stripe status, but often handled application-side.
}

export enum BillingInterval {
  Month = 'month',
  Year = 'year',
}

/**
 * Distinguishes the single base-plan line from add-on lines on a multi-item
 * subscription. The base plan drives tier limits; add-ons are billing lines that
 * can additionally whitelist features.
 */
export enum SubscriptionItemProductType {
  Plan = 'plan',
  Addon = 'addon',
}

/**
 * How a `PartnerClientLink` came to exist.
 * `created` — a partner user provisioned the client org from the hub.
 * `manual` — a SuperAdmin linked an existing org to a partner hub.
 */
export enum PartnerClientLinkSource {
  Created = 'created',
  Manual = 'manual',
}

export const subscriptionStatusEnum = z.nativeEnum(SubscriptionStatus);
export const subscriptionTierEnum = z.nativeEnum(SubscriptionTier);
export const billingIntervalEnum = z.nativeEnum(BillingInterval);
export const subscriptionItemProductTypeEnum = z.nativeEnum(
  SubscriptionItemProductType,
);
export const partnerClientLinkSourceEnum = z.nativeEnum(
  PartnerClientLinkSource,
);

export enum CallLogEvent {
  CallStarted = 'call_started',
  CallEnded = 'call_ended',
  AI = 'ai',
  Log = 'log',
  Error = 'error',
  Usage = 'usage',
  /**
   * The agent invoked a tool during the call (voice rework v2, Phase 11).
   */
  ToolCall = 'tool_call',
  /**
   * The caller barged in and cut off the agent's speech mid-turn.
   */
  Interruption = 'interruption',
  /**
   * The Live session's connection was resumed after a drop, via Gemini
   * Live's session-resumption handle.
   */
  SessionResumption = 'session_resumption',
  /**
   * The call was handed off to a human operator (cold transfer today; see
   * `voiceConfigSchema.operatorTransfer`).
   */
  CallTransfer = 'call_transfer',
}

/** Native-audio Live voices: https://firebase.google.com/docs/ai-logic/live-api/configuration#voice-options */
export enum GeminiVoices {
  Achernar = 'Achernar',
  Achird = 'Achird',
  Algenib = 'Algenib',
  Algieba = 'Algieba',
  Alnilam = 'Alnilam',
  Aoede = 'Aoede',
  Autonoe = 'Autonoe',
  Callirrhoe = 'Callirrhoe',
  Charon = 'Charon',
  Despina = 'Despina',
  Enceladus = 'Enceladus',
  Erinome = 'Erinome',
  Fenrir = 'Fenrir',
  Gacrux = 'Gacrux',
  Iapetus = 'Iapetus',
  Kore = 'Kore',
  Laomedeia = 'Laomedeia',
  Leda = 'Leda',
  Orus = 'Orus',
  Puck = 'Puck',
  Pulcherrima = 'Pulcherrima',
  Rasalgethi = 'Rasalgethi',
  Sadachbia = 'Sadachbia',
  Sadaltager = 'Sadaltager',
  Schedar = 'Schedar',
  Sulafat = 'Sulafat',
  Umbriel = 'Umbriel',
  Vindemiatrix = 'Vindemiatrix',
  Zephyr = 'Zephyr',
  Zubenelgenubi = 'Zubenelgenubi',
}

export const GEMINI_VOICE_GENDERS: Record<
  GeminiVoices,
  Gender.Male | Gender.Female
> = {
  [GeminiVoices.Achernar]: Gender.Female,
  [GeminiVoices.Achird]: Gender.Male,
  [GeminiVoices.Algenib]: Gender.Male,
  [GeminiVoices.Algieba]: Gender.Male,
  [GeminiVoices.Alnilam]: Gender.Male,
  [GeminiVoices.Aoede]: Gender.Female,
  [GeminiVoices.Autonoe]: Gender.Female,
  [GeminiVoices.Callirrhoe]: Gender.Female,
  [GeminiVoices.Charon]: Gender.Male,
  [GeminiVoices.Despina]: Gender.Female,
  [GeminiVoices.Enceladus]: Gender.Male,
  [GeminiVoices.Erinome]: Gender.Female,
  [GeminiVoices.Fenrir]: Gender.Male,
  [GeminiVoices.Gacrux]: Gender.Female,
  [GeminiVoices.Iapetus]: Gender.Male,
  [GeminiVoices.Kore]: Gender.Female,
  [GeminiVoices.Laomedeia]: Gender.Female,
  [GeminiVoices.Leda]: Gender.Female,
  [GeminiVoices.Orus]: Gender.Male,
  [GeminiVoices.Puck]: Gender.Male,
  [GeminiVoices.Pulcherrima]: Gender.Female,
  [GeminiVoices.Rasalgethi]: Gender.Male,
  [GeminiVoices.Sadachbia]: Gender.Male,
  [GeminiVoices.Sadaltager]: Gender.Male,
  [GeminiVoices.Schedar]: Gender.Male,
  [GeminiVoices.Sulafat]: Gender.Female,
  [GeminiVoices.Umbriel]: Gender.Male,
  [GeminiVoices.Vindemiatrix]: Gender.Female,
  [GeminiVoices.Zephyr]: Gender.Female,
  [GeminiVoices.Zubenelgenubi]: Gender.Male,
};

/**
 * Gemini Live (speech-to-speech) conversational models offered on a chat's
 * per-call `voiceConfig.model` override. A small fixed set — unlike
 * `ChatModel`'s open catalog — because only these two are validated against
 * `GeminiLiveService`.
 */
export enum GeminiLiveModel {
  Gemini38Live = 'gemini-3.8-live',
  Gemini31FlashLivePreview = 'gemini-3.1-flash-live-preview',
}

export const DEFAULT_GEMINI_LIVE_MODEL = GeminiLiveModel.Gemini38Live;

/**
 * Gemini Live models that accept `thinkingConfig.thinkingLevel`.
 * `gemini-3.8-live` rejects it outright ("Thinking level is not supported
 * for this model.") and closes the socket — thinking on that generation
 * lives on the separate `gemini-3.8-live-extended-thinking` model, which
 * isn't offered here. Consulted by both the admin voice-settings form
 * (hides the field) and `GeminiLiveService` (omits `thinkingConfig`) so the
 * two can't drift.
 */
const GEMINI_LIVE_MODELS_WITH_THINKING: ReadonlySet<string> = new Set([
  GeminiLiveModel.Gemini31FlashLivePreview,
]);

export function geminiLiveModelSupportsThinking(model: string): boolean {
  return GEMINI_LIVE_MODELS_WITH_THINKING.has(model);
}

export enum OmnichannelPostbackAction {
  AcceptTakeover = 'accept_takeover',
  DeclineTakeover = 'decline_takeover',
}

export * from './enums/feedEnums';

export enum Currency {
  USD = 'usd',
  CZK = 'czk',
}

/**
 * Organization communication language. Drives the Stripe customer's
 * `preferred_locales` (invoice emails, dunning, Hosted Invoice Page, invoice
 * PDF) and can be extended as the admin-app's supported UI locales grow.
 */
export enum Locale {
  Cs = 'cs',
  En = 'en',
}

export enum ChargeType {
  BalanceTopUp = 'balance_top_up',
  Subscription = 'subscription',
  AutoRenewal = 'auto_renewal',
}

export enum WebsitePageStatus {
  New = 'new',
  Existing = 'existing',
  Removed = 'removed',
  Untracked = 'untracked',
}

export enum RemovalBehavior {
  Flag = 'flag',
  Remove = 'remove',
  Ignore = 'ignore',
}
